import * as React from "react";
import useInView from "@react-hook/intersection-observer";
import { EditorMode } from "@/Apps/Pages/Editor/types";

export interface LazyLoaderProps {
  children: React.ReactNode;
  mode?: EditorMode;
  threshold?: number;
  rootMargin?: string;
  placeholder?: React.ReactNode;
  placeholderStyle?: React.CSSProperties;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * LazyLoader component that only renders its children when they come into view
 * Uses Intersection Observer API to detect when the element is visible
 * 
 * @param children - The content to render when in view
 * @param mode - Editor mode (always renders in editor mode for proper editing)
 * @param threshold - How much of the element needs to be visible (0-1, default: 0.1 = 10%)
 * @param rootMargin - Margin around the viewport to trigger loading early (default: "50px")
 * @param placeholder - Custom placeholder content to show when not in view
 * @param placeholderStyle - Custom styles for the placeholder
 * @param className - CSS class name for the wrapper
 * @param style - Inline styles for the wrapper
 */
export const LazyLoader: React.FC<LazyLoaderProps> = ({
  children,
  mode = EditorMode.LIVE,
  threshold = 0.1,
  rootMargin = "50px",
  placeholder,
  placeholderStyle,
  className,
  style,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    threshold,
    rootMargin,
  });

  // Default placeholder
  const defaultPlaceholder = (
    <div 
      style={{ 
        minHeight: "100px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#999",
        fontSize: "14px",
        ...placeholderStyle
      }}
    >
      Loading...
    </div>
  );

  // Always render in editor mode to ensure proper editing experience
  if (mode === EditorMode.EDITOR || mode === EditorMode.SIMPLE) {
    return (
      <div ref={ref} className={className} style={style}>
        {children}
      </div>
    );
  }

  // In live mode, only render when in view
  if (!inView) {
    return (
      <div 
        ref={ref} 
        className={className}
        style={style}
      >
        {placeholder || defaultPlaceholder}
      </div>
    );
  }

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
};

export default LazyLoader;
